import noop from 'lodash/noop';
import { createContext } from 'react';

import { type UndoRedoButtonGroupProps } from '@/components/form/baseInputs/Button/UndoRedoButtonGroup';
import { useGuardedContext } from '@/hooks/useGuardedContext';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';

export interface DiagramEditorContext {
  /** The saved artifact. Null for a new diagram until its first generation saves it. */
  artifactID: string | null;
  /** The diagram that the user edits. Null until the saved artifact loads. */
  diagram: DiagramBuilder_DiagramFragment | null;
  /**
   * The artifact name from `DiagramArtifact.name`, not the diagram title.
   * Null until the saved artifact loads.
   */
  name: string | null;
  loading: boolean;
  /** True while the backend generates this diagram. Edits and sends are locked. */
  isGenerating: boolean;
  isSaving: boolean;
  isRenaming: boolean;
  /** True when the editor diagram differs from the saved diagram. */
  isDirty: boolean;
  error: string | null;
  history: UndoRedoButtonGroupProps;
  update: (diagram: DiagramBuilder_DiagramFragment) => void;
  /** Saves the editor diagram. Returns false when the save fails. */
  save: () => Promise<boolean>;
  /**
   * Renames the artifact. The rename does not save the diagram or change its
   * revision, so it is available during generation.
   */
  rename: (name: string) => Promise<void>;
  close: () => void;
}

export const DiagramEditorContext = createContext<DiagramEditorContext>({
  artifactID: null,
  diagram: null,
  name: null,
  loading: false,
  isGenerating: false,
  isSaving: false,
  isRenaming: false,
  isDirty: false,
  error: null,
  history: { canUndo: false, canRedo: false, onUndo: noop, onRedo: noop },
  update: noop,
  save: () => Promise.resolve(false),
  rename: () => Promise.resolve(),
  close: noop,
});

export const useDiagramEditorContext = () =>
  useGuardedContext(DiagramEditorContext, 'DiagramEditorProvider');
